import { CalendarOutlined, TableOutlined } from "@ant-design/icons";
import { Button, Col, Empty, Image, Modal, Row, Table, Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import { useParams } from "react-router-dom";
import SecondaryButton from "../Buttons/SecondaryButton";
import { MOODS } from "./constants";
import {
  createPhysicalActivityTableColumns,
  formatCalendarEventTime,
} from "./helpers";
import CalendarToolbar from "./Views/CalendarToolbar";

const localizer = momentLocalizer(moment);

import { get_language, translate } from "../../locale";
import LogBookServices from "../../services/LogBookServices";
import UserServices from "../../services/UserServices";

const { Title, Text } = Typography;

const PhysicalActivityCalendar = () => {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(
    moment().startOf("isoWeek").format("YYYY-MM-DD")
  );
  const [patient, setPatient] = useState(null);
  const [dataTable, setDataTable] = useState([]);
  const [modalDetails, setModalDetails] = useState(null);
  const [showAs, setShowAs] = useState("calendar");

  const { username } = useParams();

  // Función común para ordenamiento por fecha
  const dateSorter = (a, b) => {
    if (a.start_datetime < b.start_datetime) return -1;
    if (a.start_datetime > b.start_datetime) return 1;
    return 0;
  };

  // Función común para formateo de fecha
  const formatDate = (record, format = "DD/MM/YYYY") => {
    return moment(`${record.activity_date} ${record.activity_time}`).format(
      get_language === "en" && format === "DD/MM/YYYY" ? "MM/DD/YYYY" : format
    );
  };

  // Función para formateo completo de fecha y hora
  const formatDateTime = (record) => {
    return moment(`${record.activity_date} ${record.activity_time}`).format(
      `${get_language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"} HH:mm`
    );
  };

  useEffect(() => {
    loadPatientInfo();
  }, []);

  const loadPatientInfo = async () => {
    try {
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        setPatient(res.data.my_contact_info);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  const fetchData = async () => {
    try {
      const response = await LogBookServices.getPhysicalActivity(
        username,
        date
      );
      if (response.status >= 200 && response.status <= 300) {
        let newData = response.data.activity.map((i) => {
          const register_datetime = `${i.activity_date} ${i.activity_time}`;
          return {
            ...i,
            title: new Date(register_datetime),
            start: new Date(register_datetime),
            end: new Date(register_datetime),
            register_datetime: register_datetime,
            type: "cell",
          };
        });

        let caloriesData = {};
        newData.forEach((i) => {
          const date = moment(`${i.register_datetime}`).format("YYYY-MM-DD");
          const tz = `${i.register_datetime}`.substring(19, 25);
          if (caloriesData[date]) {
            const calories =
              Number.parseFloat(caloriesData[date].calories) +
              Number.parseFloat(i.calories);
            caloriesData[date] = { ...caloriesData[date], calories: calories };
          } else {
            caloriesData = {
              ...caloriesData,
              [date]: {
                calories: i.calories,
                date_time: date + "T00:00:00" + tz,
              },
            };
          }
        });

        const caloriesLabels = Object.keys(caloriesData).map((i, k) => {
          return {
            id: "dsg_" + k,
            title: caloriesData[i].calories,
            start: new Date(caloriesData[i].date_time),
            end: new Date(caloriesData[i].date_time),
            allDay: true,
            date_time: caloriesData[i].date_time,
            calories: caloriesData[i].calories,
            type: "header",
          };
        });
        setDataTable(response.data.activity);
        // agregamos los labels
        newData = [...newData, ...caloriesLabels];
        setData(newData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDetails = (item) => {
    setModalDetails(item);
  };

  const columns = createPhysicalActivityTableColumns(
    handleDetails,
    MOODS,
    dateSorter,
    formatDate
  );

  const handleHideDetails = () => {
    setModalDetails(null);
  };

  const handlePrev = () => {
    setDate((v) => moment(v).subtract(7, "days").format("YYYY-MM-DD"));
  };
  const handleToday = () => {
    setDate((v) => moment().format("YYYY-MM-DD"));
  };
  const handleNext = () => {
    if (date !== moment().format("YYYY-MM-DD")) {
      setDate((v) => moment(v).add(7, "days").format("YYYY-MM-DD"));
    }
  };

  const getCalendarToolbar = (props) => {
    return (
      <CalendarToolbar
        {...props}
        onPrev={handlePrev}
        onToday={handleToday}
        onNext={handleNext}
      />
    );
  };

  const getCalendarWeekEvent = ({ event }) => {
    return (
      <Button
        type="text"
        className={`text-center w-full inline p-0 m-0 h-auto leading-none text-white hover:text-white/70 ${
          event.type === "cell" ? "" : "rbc-event-content-header"
        }`}
        onClick={(e) => {
          e.preventDefault();
          if (event.type === "cell") {
            handleDetails(event);
          }
        }}
      >
        {formatCalendarEventTime(event)}
      </Button>
    );
  };

  return (
    <Row className="w-full">
      <Col flex="column" className="w-full" id="PhysicalActivity">
        <Row className="w-full">
          <Col span={24} className="mt-4">
            <Title level={2} className="text-center">
              {translate("Actividad física de ")}{" "}
              {patient && `${patient.user.full_name}`}
            </Title>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col span={24} className="flex flex-row justify-end space-x-2">
            <span className="flex flex-col justify-center">
              {translate("Formato")}:
            </span>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setShowAs("calendar")}
              type={showAs === "calendar" ? "primary" : "default"}
            />
            <Button
              icon={<TableOutlined />}
              onClick={() => setShowAs("table")}
              type={showAs === "table" ? "primary" : "default"}
            />
          </Col>
        </Row>
        {showAs === "calendar" && (
          <Calendar
            localizer={localizer}
            defaultDate={null}
            defaultView={Views.WEEK}
            views={["week"]}
            events={data || []}
            startAccessor="start"
            endAccessor="end"
            step={60}
            timeslots={6}
            style={{ height: 500 }}
            components={{
              toolbar: getCalendarToolbar,
              week: {
                event: getCalendarWeekEvent,
              },
            }}
            formats={{
              eventTimeRangeFormat: () => "",
              timeGutterFormat: () => "",
            }}
          />
        )}
        {showAs === "table" && (
          <>
            <div className="flex flex-row my-4">
              <div>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handlePrev();
                  }}
                >
                  &lt;
                </SecondaryButton>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handleToday();
                  }}
                >
                  Hoy
                </SecondaryButton>
                <SecondaryButton
                  className=""
                  onClick={() => {
                    handleNext();
                  }}
                >
                  &gt;
                </SecondaryButton>
              </div>
            </div>
            <Table
              rowKey={(i) => i.id}
              className="w-full mt-6"
              dataSource={dataTable}
              columns={columns}
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
                filterTitle: translate("Filtros"),
                filterConfirm: translate("Aceptar"),
                filterReset: translate("Limpiar"),
                filterEmptyText: translate("No hay filtros"),
                filterCheckall: translate("Seleccinar todos"),
                filterSearchPlaceholder: translate("Buscar en filtros"),
              }}
            />
          </>
        )}
        <Modal
          width={600}
          open={modalDetails !== null}
          title={translate("Detalles de la actividad")}
          onCancel={handleHideDetails}
          footer={[
            <Button key="ok" type="primary" onClick={handleHideDetails}>
              {translate("Aceptar")}
            </Button>,
          ]}
        >
          {modalDetails !== null && (
            <Row>
              <Col className="flex-1">
                <Row>
                  <Col>
                    <Text>
                      <Text strong>Fecha:</Text> {formatDateTime(modalDetails)}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      <Text strong>Calorías:</Text> {modalDetails.calories}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      Estado de ánimo:{" "}
                      {modalDetails.mood ? MOODS[modalDetails.mood] : "-"}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      <Text strong>Texto:</Text> {modalDetails.text}
                    </Text>
                  </Col>
                </Row>
                {modalDetails.image_url && (
                  <Row>
                    <Col>
                      <Image
                        src={modalDetails.image_url}
                        className="max-h-80"
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          )}
        </Modal>
      </Col>
    </Row>
  );
};

export default PhysicalActivityCalendar;
